/**
 * Leaf module for the Magic Sign primitives shared by `constants.ts` and `utils.ts`.
 *
 * Why this file exists: `constants.ts` re-exports helpers from `utils.ts` (mapConfigMetaRaw,
 * deriveSigningLinks, etc.), and those helpers need these same values. Importing `constants.ts`
 * from `utils.ts` would create a circular import, so the shared primitives live here instead —
 * a leaf with no dependency on either file — and both import from it directly.
 */
import { SignType } from './types';

/**
 * Display labels for every stored sign-type value. Aadhaar and DSC both route through Leegality
 * and are presented to the user as a single "Aadhaar/DSC" choice, so both map to the same label
 * (a legacy row stored as DSC still renders correctly).
 */
export const SIGN_TYPE_LABELS: Record<SignType, string> = {
    [SignType.E_SIGN]: 'E-Sign',
    [SignType.AADHAAR]: 'Aadhaar/DSC',
    [SignType.DSC]: 'Aadhaar/DSC',
} as const;

/**
 * Fallback capacity options used when the backend does not supply
 * ConfigMeta.signingCapacities. These mirror the Leegality-standard roles.
 */
export const DEFAULT_SIGNING_CAPACITIES: { id: string; label: string }[] = [
    { id: 'AUTHORIZED_SIGNATORY', label: 'Authorized Signatory' },
    { id: 'PERSONAL_GUARANTOR', label: 'Personal Guarantor' },
    { id: 'DIRECTOR', label: 'Director' },
    { id: 'WITNESS', label: 'Witness' },
];

/**
 * Maximum number of signatories per document (Leegality platform limit).
 * Read from ConfigMeta.maxSignatories at runtime; this is the safe default.
 */
export const MAX_SIGNATORIES_DEFAULT = 4;
